import { useContext, useEffect, useRef, useState } from "react";
import { Button, Spinner, Typography } from "@material-tailwind/react";
import { CuisinesData, DietData, IntolerancesData } from "../data";
import recipes from "../API/recipes";
import { RecepiesContext } from "../context/RepiesContext";
import { focusSearch } from "../utils";

/** Outside-click hook */
function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", handler, { passive: true });
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClose]);
}

const Hero = () => {
  const [loading, setLoading] = useState(false);
  const { setRecepies } = useContext(RecepiesContext);

  // Filter state
  const [cuisine, setCuisine] = useState(undefined);
  const [diet, setDiet] = useState(undefined);
  const [ints, setInts] = useState([]); // array of strings

  // Dropdown state
  const [open, setOpen] = useState(null); // "cuisine" | "diet" | "ints" | null
  const barRef = useRef(null);
  useOutsideClose(barRef, () => setOpen(null));

  // Intolerance search query
  const [intsQuery, setIntsQuery] = useState("");

  useEffect(() => {
    recipes
      .getRecepies()
      .then((res) =>
        setRecepies({ ...res.data, cuisine, diet, intolerances: ints })
      )
      .catch(console.log)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRecepies]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { cuisine, diet, intolerances: ints };
    try {
      const res = await recipes.getRecepies(payload);
      setRecepies({ ...res.data, ...payload });
      focusSearch();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    setCuisine(undefined);
    setDiet(undefined);
    setInts([]);
    setIntsQuery("");
  };

  const intsCount = ints.length;
  const pill =
    "inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm text-white/90 hover:bg-white/15 hover:border-white/50 transition-colors";
  const badge =
    "ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-white/90 px-1.5 text-xs font-semibold text-blue-700";

  return (
    <section className="relative pt-20">
      <div className="relative flex min-h-[520px] h-[calc(85vh-80px)] items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `
              radial-gradient(1200px 600px at 10% -10%, rgba(191,219,254,0.55), transparent 60%),
              radial-gradient(1200px 600px at 90% 0%, rgba(147,197,253,0.45), transparent 60%),
              linear-gradient(135deg, #0b5bd3 0%, #0f6ce8 35%, #1e88ff 65%, #2ea6ff 100%)
            `,
          }}
        />

        {/* Card */}
        <div className="mx-4 w-full max-w-3xl rounded-2xl border border-white/20 bg-white/12 p-8 shadow-[0_10px_40px_rgba(0,0,0,0.25)] backdrop-blur-xl">
          <Typography variant="h3" className="mb-1 text-center font-extrabold text-white">
            Find Recipes
          </Typography>
          <p className="mb-6 text-center text-white">
            Filter by cuisine, diet, and intolerances.
          </p>

          {/* FILTER BAR */}
          <div
            ref={barRef}
            className="relative mb-6 flex flex-col gap-3 rounded-xl border border-white/25 bg-white/10 p-3 sm:flex-row sm:items-center sm:gap-3"
          >
            {/* Cuisine */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpen(open === "cuisine" ? null : "cuisine")}
                className={pill}
              >
                <span className="opacity-80">Cuisine</span>
                {cuisine ? (
                  <span className="font-semibold">{String(cuisine)}</span>
                ) : (
                  <span className="text-white/60">Any</span>
                )}
                <svg className="ml-1 h-4 w-4 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {open === "cuisine" && (
                <div className="absolute left-0 z-[300] mt-2 w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                  <div className="max-h-60 overflow-auto">
                    {CuisinesData.map((c) => (
                      <button
                        key={c}
                        onClick={() => {
                          setCuisine(c);
                          setOpen(null);
                        }}
                        className={`block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100 ${cuisine === c ? "bg-blue-50 text-blue-700" : "text-gray-800"
                          }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                  {cuisine && (
                    <div className="mt-2 text-right">
                      <button
                        className="text-xs font-semibold text-blue-700 hover:underline"
                        onClick={() => setCuisine(undefined)}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Diet */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpen(open === "diet" ? null : "diet")}
                className={pill}
              >
                <span className="opacity-80">Diet</span>
                {diet ? (
                  <span className="font-semibold">{String(diet)}</span>
                ) : (
                  <span className="text-white/60">Any</span>
                )}
                <svg className="ml-1 h-4 w-4 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {open === "diet" && (
                <div className="absolute left-0 z-[300] mt-2 w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                  <div className="max-h-60 overflow-auto">
                    {DietData.map((d) => (
                      <button
                        key={d}
                        onClick={() => {
                          setDiet(d);
                          setOpen(null);
                        }}
                        className={`block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100 ${diet === d ? "bg-blue-50 text-blue-700" : "text-gray-800"
                          }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                  {diet && (
                    <div className="mt-2 text-right">
                      <button
                        className="text-xs font-semibold text-blue-700 hover:underline"
                        onClick={() => setDiet(undefined)}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Intolerances (custom multi-select) */}
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => setOpen(open === "ints" ? null : "ints")}
                className={`${pill} w-full justify-between`}
              >
                <span className="opacity-80">Intolerances</span>
                <span className="flex items-center">
                  {intsCount > 0 ? (
                    <span className={badge}>{intsCount}</span>
                  ) : (
                    <span className="text-white/60">Any</span>
                  )}
                  <svg className="ml-2 h-4 w-4 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              </button>

              {open === "ints" && (
                <div className="absolute left-0 right-0 z-[350] mt-2 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                  {/* Search box */}
                  <input
                    type="text"
                    value={intsQuery}
                    onChange={(e) => setIntsQuery(e.target.value)}
                    placeholder="Search intolerances…"
                    className="mb-2 w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />

                  {/* 2-column compact list with checkboxes */}
                  <div className="grid max-h-56 grid-cols-1 gap-1 overflow-auto px-1 sm:grid-cols-2">
                    {IntolerancesData.filter((i) =>
                      i.toLowerCase().includes(intsQuery.trim().toLowerCase())
                    ).map((i) => {
                      const checked = ints.includes(i);
                      return (
                        <label
                          key={i}
                          className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm ${checked ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50"
                            }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-blue-600"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setInts((prev) => [...prev, i]);
                              else setInts((prev) => prev.filter((x) => x !== i));
                            }}
                          />
                          <span>{i}</span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Footer row */}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-gray-600">{intsCount} selected</div>
                    <div className="flex gap-3">
                      <button
                        className="text-xs font-semibold text-blue-700 hover:underline"
                        onClick={() => setInts([])}
                      >
                        Clear
                      </button>
                      <button
                        className="text-xs font-semibold text-blue-700 hover:underline"
                        onClick={() => setOpen(null)}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-4">
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="flex-1 bg-white text-blue-700 font-semibold hover:bg-white/90"
            >
              {loading ? <Spinner className="!text-blue-700" /> : "Search"}
            </Button>
            <Button
              onClick={handleClear}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
